import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  NumberCardList,
  getNumberVerification,
  isNumberVerified,
} from "@tiny/sms/src/widgets";
import { ProviderNumber, SmsAccountView } from "../../../app/GeneratedApi";

// Card id for "no explicit number" — the node then sends from the account's
// default number.
const DEFAULT_SENDER_ID = "__account_default__";

export interface DetailsStepProps {
  numbers: ProviderNumber[];
  numbersLoading: boolean;
  smsAccount?: SmsAccountView;
  senderNumberId?: string;
  onSelectSender: (id?: string) => void;
}

function DetailsStep(props: DetailsStepProps) {
  const {
    numbers,
    numbersLoading,
    smsAccount,
    senderNumberId,
    onSelectSender,
  } = props;

  const accountNumber = numbers.find(
    (number) => number.phoneNumber === smsAccount?.phoneNumber
  );
  const accountNumberId = accountNumber?.providerNumberId;
  const hasVerified = numbers.some(isNumberVerified);
  const selectedId =
    !senderNumberId || senderNumberId === accountNumberId
      ? DEFAULT_SENDER_ID
      : senderNumberId;

  // Only verified numbers can be picked; the rest say why they can't send.
  const defaultVerified = !!accountNumber && isNumberVerified(accountNumber);
  const options = [
    {
      id: DEFAULT_SENDER_ID,
      phoneNumber: smsAccount?.phoneNumber || "Account default",
      caption: defaultVerified
        ? "Your account's default number"
        : `Default number · ${
            accountNumber
              ? getNumberVerification(accountNumber).hint
              : "Can't send texts until carriers verify it."
          }`,
      badge: defaultVerified
        ? "Default"
        : accountNumber
        ? getNumberVerification(accountNumber).label
        : "Not verified",
      disabled: !defaultVerified,
    },
    ...numbers
      // The default card already covers the account's own number.
      .filter((number) => number.phoneNumber !== smsAccount?.phoneNumber)
      .map((number) => {
        const verified = isNumberVerified(number);
        const verification = getNumberVerification(number);
        return {
          id: number.providerNumberId,
          phoneNumber: number.phoneNumber,
          disabled: !verified,
          badge: verified ? undefined : verification.label,
          caption: verified ? undefined : verification.hint,
        };
      }),
  ];

  return (
    <Box sx={{ maxWidth: 760, margin: "0 auto", padding: "32px 24px" }}>
      <Typography variant="h3" fontWeight="bold" color="grey" mb={2}>
        Send from
      </Typography>
      <Typography variant="p2" color="grey" component="p" mt={0} mb={6}>
        Pick which number this step texts from, then continue to write the
        message.
      </Typography>

      {numbersLoading ? (
        <CircularProgress size={24} />
      ) : numbers.length === 0 ? (
        <Box
          sx={(theme) => ({
            padding: "24px",
            borderRadius: "12px",
            backgroundColor: theme.palette.grey[100],
          })}
        >
          <Typography variant="p2" color="grey">
            {smsAccount?.activated
              ? "No numbers on this account yet — this step will be skipped until one is added."
              : "SMS isn't set up on this account yet — this step will be skipped until it is."}
          </Typography>
        </Box>
      ) : (
        <>
          <NumberCardList
            aria-label="Sender number"
            options={options}
            selectedId={selectedId}
            onSelect={(id) =>
              onSelectSender(id === DEFAULT_SENDER_ID ? undefined : id)
            }
          />
          {!hasVerified && (
            <Typography
              variant="p2"
              color="grey.80"
              component="p"
              mt={3}
              mb={0}
            >
              None of your numbers is verified yet. Verify one under SMS numbers
              to continue to the message.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

export default DetailsStep;
